import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Checkbox, Table } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FloatingActionBar, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { showMessage } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import { DEFAULT_LIST_OFFSET, DEFAULT_PAGINATION_PAGE, DEFAULT_VALUE_0, LIST_LIMIT_0, LIST_LIMIT_20 } from 'common/config';
import { Bin, Cancelled } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryActionCard, InventoryEmptyList } from 'entities/Inventory/components';
import { EInventoryActionStatus, EInventoryActionType } from 'entities/Inventory/Inventory.enum';
import { IInventoryAction, IInventoryActionListPayload } from 'entities/InventoryAction/InventoryAction.models';
import { renderInventoryActionListRecords } from 'entities/Inventory/Inventory.helper';
import {
  deleteInventoryAction,
  filterInventoryActionList,
  getInventoryActionById,
  getInventoryActionList,
  getInventoryActionListBatch,
  selectInventoryActionCount,
  selectInventoryActionList,
  selectIsInventoryActionLoading,
  setInventoryActionList,
} from 'entities/InventoryAction/InventoryAction.slice';
import { getUserInventoryStats, selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  menu: ReactNode;
}

export const WriteOffInventoryActionListForUserTab: FC<IComponentProps> = ({ header, menu }) => {
  const dispatch = useAppDispatch();
  // inventoryAction
  const inventoryActionList = useAppSelector(selectInventoryActionList);
  const inventoryActionCount = useAppSelector(selectInventoryActionCount);
  const isInventoryActionLoading = useAppSelector(selectIsInventoryActionLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [selectedInventoryActionList, setSelectedInventoryActionList] = useState<IInventoryAction[]>([]);
  const [payload, setPayload] = useState<IInventoryActionListPayload>({
    types: [EInventoryActionType.WriteOff],
    statuses: [EInventoryActionStatus.New],
    userId: currentUser?.id,
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenInventoryActionCard } = useInventoryContext();

  const checkAll = inventoryActionList.length === selectedInventoryActionList.length;
  const indeterminate =
    selectedInventoryActionList.length > DEFAULT_VALUE_0 && selectedInventoryActionList.length < inventoryActionList.length;

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    e.target.checked ? setSelectedInventoryActionList(inventoryActionList) : setSelectedInventoryActionList([]);
  };

  const onInventoryActionSelectionChange = (id: number, checked: boolean) => {
    const action = inventoryActionList.find((item) => item.id === id) as IInventoryAction;
    const newSelectedInventoryActionList = checked
      ? [...selectedInventoryActionList, action]
      : selectedInventoryActionList.filter((item) => item.id !== id);

    setSelectedInventoryActionList(newSelectedInventoryActionList);
  };

  const onSearchClick = async (value?: string) => {
    const newPayload = { ...payload, search: value };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    setSelectedInventoryActionList([]);
    await dispatch(getInventoryActionList(newPayload));
  };

  const onChangeRequestStatusSuccess = async (records: IInventoryAction[]) => {
    const recordIds = records.map((record) => record.id);
    const newSelectedInventoryActionList = selectedInventoryActionList.filter((action) => !recordIds.includes(action.id));
    const filteredInventoryActionList = inventoryActionList.filter((action) => !recordIds.includes(action.id));

    await dispatch(getUserInventoryStats({}));
    setSelectedInventoryActionList(newSelectedInventoryActionList);
    dispatch(filterInventoryActionList(records));
    setOpenInventoryActionCard(false);

    if (filteredInventoryActionList.length === LIST_LIMIT_0) {
      const newPayload = { ...payload, offset: DEFAULT_LIST_OFFSET };

      setPage(DEFAULT_PAGINATION_PAGE);
      setPayload(newPayload);
      dispatch(getInventoryActionList(newPayload));
    }
  };

  const onCancelRequestClick = (records: IInventoryAction[]) => {
    const recordIds = records.map((record) => record.id);

    dispatch(
      deleteInventoryAction({
        ids: recordIds,
        onSuccess: () => {
          showMessage({
            content: 'Запрос о списании оборудования отменен.',
            icon: <Bin className="icon-bin-dark-grey" />,
          });
          onChangeRequestStatusSuccess(records);
        },
      }),
    );
  };

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);
      setPage(DEFAULT_PAGINATION_PAGE);
      dispatch(setInventoryActionList([]));
      setSelectedInventoryActionList([]);
      await dispatch(getUserInventoryStats({}));
      await dispatch(getInventoryActionList(payload));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isInventoryActionLoading && inventoryActionList.length < inventoryActionCount}
        loader={<Spin />}
        scrollToTopTrigger={[payload]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getInventoryActionListBatch({
              ...payload,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}
        {menu}

        <div className="mb-12">
          <SearchInput placeholder="Искать по названию или артикулу" onSearch={onSearchClick} />
        </div>

        {!!inventoryActionList.length && (
          <div className="sticky px-3 pb-4 flex align-center inventory-list__actions">
            <Checkbox checked={checkAll} indeterminate={indeterminate} onChange={onCheckAllChange}>
              Все запросы
            </Checkbox>
          </div>
        )}

        <Table
          className={`
                table-hover
                ${!!inventoryActionList.length && inventoryActionList.length === inventoryActionCount && 'table-end-of-list'}
                ${!inventoryActionList.length && 'table-empty'}
              `}
          dataSource={inventoryActionList.map((inventory) => ({ ...inventory, key: inventory.id }))}
          columns={renderInventoryActionListRecords({ onCancelRequestClick })}
          showHeader={false}
          pagination={false}
          loading={isInventoryActionLoading}
          rowSelection={{
            selectedRowKeys: selectedInventoryActionList.map((action) => action.id),
            onSelect: ({ id }, checked) => onInventoryActionSelectionChange(id, checked),
            columnWidth: 40,
          }}
          onRow={({ id }) => ({
            onClick: async () => {
              await dispatch(getInventoryActionById(id));
              setOpenInventoryActionCard(true);
            },
          })}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <InventoryEmptyList title="Активных запросов нет" />,
          }}
        />
      </InfiniteScroll>

      <InventoryActionCard onCancel={onCancelRequestClick} />

      <FloatingActionBar selectedCount={selectedInventoryActionList.length} count={inventoryActionList.length}>
        <Button
          className="floating-action-bar__btn red"
          icon={<Cancelled className="icon-red" />}
          onClick={() => onCancelRequestClick(selectedInventoryActionList)}
          loading={isInventoryActionLoading}
        >
          Отменить все
        </Button>
      </FloatingActionBar>
    </>
  );
};
